import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

import { auth as authConfig } from '@/config/config';

export default class CognitoAuth {
  constructor() {
    this.cognitoUser = null;
    this.correlationCode = '';
    this.currentEmail = '';
    this.currentPassword = '';
    this.methodUsed = null;
    this.userPool = new CognitoUserPool({
      UserPoolId: authConfig.cognito.userPoolId,
      ClientId: authConfig.cognito.clientId,
    });
  }

  static install(Vue) {
    // eslint-disable-next-line no-param-reassign,no-use-before-define
    Vue.prototype.$cognitoAuth = CognitoService;
  }

  getCurrentUser() {
    return this.userPool.getCurrentUser();
  }

  async getUserData() {
    const cognitoUser = this.getCurrentUser();
    await new Promise((resolve, reject) => {
      cognitoUser.getSession((err) => {
        if (err) {
          reject(err);
        }
        resolve(true);
      });
    });
    return new Promise((resolve, reject) => {
      cognitoUser.getUserData((err, res) => {
        if (err) {
          reject(err);
        }
        const userAttrs = {};
        res.UserAttributes.forEach((attr) => {
          userAttrs[attr.Name] = attr.Value;
        });
        resolve(userAttrs);
      });
    });
  }

  async isAuthenticated() {
    const cognitoUser = this.getCurrentUser();

    if (cognitoUser) {
      return new Promise((resolve) => {
        cognitoUser.getSession((err) => {
          if (err) {
            return resolve(false);
          }

          return resolve(true);
        });
      });
    }

    return false;
  }

  async login(email, password, mfaMethod = 'EMAIL') {
    // Store credentials for potential resend
    this.currentEmail = email;
    this.currentPassword = password;
    this.methodUsed = mfaMethod;

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
      ValidationData: {
        mfaMethod, // This becomes clientMetadata in Lambda
      },
    });

    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    this.cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');

    const setCode = (code) => {
      this.correlationCode = code;
    };

    const setMethod = (method) => {
      this.methodUsed = method;
    };

    return new Promise((resolve, reject) => {
      this.cognitoUser.authenticateUser(authDetails, {
        customChallenge(challengeParameters) {
          setCode(challengeParameters.correlationCode);
          const usedMethod = challengeParameters.method || mfaMethod;
          setMethod(usedMethod);
          resolve({
            status: 'NEEDS_MFA',
            correlationCode: challengeParameters.correlationCode,
            methodUsed: usedMethod,
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        onSuccess: (result) => {
          resolve({
            status: 'SUCCESS',
            token: result.getIdToken().getJwtToken(),
          });
        },
      });
    });
  }

  async handleMfa(mfaCode) {
    return new Promise((resolve, reject) => {
      this.cognitoUser.sendCustomChallengeAnswer(
        mfaCode,
        {
          onFailure: (err) => {
            reject(err);
          },
          onSuccess: (result) => {
            resolve(result.getIdToken().getJwtToken());
          },
        },
        {
          correlationCode: this.correlationCode,
        },
      );
    });
  }

  async resendCode(newMethod) {
    // Resend code with different method by re-authenticating
    if (!this.currentEmail || !this.currentPassword) {
      throw new Error('No active login session to resend code');
    }
    return this.login(this.currentEmail, this.currentPassword, newMethod);
  }

  logout() {
    // Clear stored credentials
    this.currentEmail = '';
    this.currentPassword = '';
    this.correlationCode = '';
    this.methodUsed = null;
    return new Promise(resolve => this.getCurrentUser().signOut(resolve));
  }

  async resetPassword(email) {
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      this.cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  async confirmResetPassword(email, verificationCode, newPassword) {
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      this.cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  async resendConfirmationCode(email) {
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      this.cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  async verifyEmail(email, verificationCode) {
    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      this.cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          if (err.name === 'ExpiredCodeException') {
            resolve(err);
            return;
          }

          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  async getIdToken() {
    const cognitoUser = this.getCurrentUser();
    if (cognitoUser === null) {
      return null;
    }
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) return reject(err);

        if (session.isValid()) {
          return resolve(session.getIdToken().getJwtToken());
        }

        return reject(new Error('Session is invalid'));
      });
    });
  }
}

export const CognitoService = new CognitoAuth();



// WEBPACK FOOTER //
// ./src/services/cognito.js